import React from "react";
import { ExternalLink } from "lucide-react";
import { trackOutboundClick } from "../../lib/outboundTracker";
import { recordSource } from "../../investigation/investigationMemory";
import { DossierLabel } from "../dossier/DossierLabel";
import { PaperPatina } from "../realism/PaperPatina";
import { EvidencePreservation } from "./EvidencePreservation";

const STANCE_CLASSES = {
  mainstream: "text-[#A39E93] border-[#454038]",
  skeptical: "text-[#CBB892] border-[#CBB892]/40",
  investigative: "text-[#D92D20] border-[#D92D20]/50",
  primary: "text-[#E4DFD3] border-[#E4DFD3]/30",
  academic: "text-[#8FB4A8] border-[#8FB4A8]/40",
  book: "text-[#D9A26C] border-[#D9A26C]/40",
  reference: "text-[#9FA8D4] border-[#9FA8D4]/40",
};

const stanceColor = (s) => STANCE_CLASSES[s] || STANCE_CLASSES.mainstream;

// Map a source's stance into a coarse link_type the backend can roll up.
const linkTypeFor = (stance, url = "") => {
  if (stance === "book") return "book";
  if (/\.pdf(\?|$)/i.test(url)) return "pdf";
  if (stance && ["academic", "primary", "investigative", "mainstream", "skeptical", "reference"].includes(stance)) {
    return stance;
  }
  return "source";
};

const SourceCard = ({ source, index, topicSlug, topicId }) => {
  const linkType = linkTypeFor(source.stance, source.url);
  const onClick = () => {
    recordSource();
    trackOutboundClick(source.url, {
      topic_id: topicId || null,
      topic_slug: topicSlug || null,
      link_type: linkType,
      source_index: index,
      publisher: source.publisher || null,
      stance: source.stance || null,
    });
  };
  return (
    <a
      href={source.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      onAuxClick={onClick}
      data-testid={`source-link-${index}`}
      data-tracking-type={linkType}
      className="evidence-card border border-[#2D2A26] bg-[#131313] p-5 flex gap-3 hover:border-[#D92D20] paper-patina-host relative overflow-hidden"
    >
      <PaperPatina seed={`source-${topicSlug || "src"}-${index}-${(source.url || "").slice(0, 24)}`} variant="dark" intensity={0.5} />
      <ExternalLink size={14} className="text-[#D92D20] mt-1 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="font-heading text-lg text-[#E4DFD3] mb-1">{source.title}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#757065] truncate">
          {source.publisher}
        </div>
        {source.stance && (
          <div className={`mt-2 inline-block px-2 py-0.5 border font-mono text-[9px] uppercase tracking-widest ${stanceColor(source.stance)}`}>
            {source.stance}
          </div>
        )}
        {(source.recovered || source.archived_url) && (
          <div className="mt-2 font-mono text-[9px] uppercase tracking-widest text-[#9FA8D4]"
               data-testid={`source-archived-note-${index}`}>
            {source.recovered
              ? "Original source unavailable — archived copy cited"
              : "Archived reference on file"}
          </div>
        )}
      </div>
    </a>
  );
};

export const SourcesGrid = ({ sources = [], topicSlug = null, topicId = null }) => (
  <section className="mt-12" data-testid="dossier-sources-section">
    <DossierLabel as="h2" className="mt-0">Sources</DossierLabel>
    <h3 className="font-heading text-3xl text-[#E4DFD3] mt-4 mb-6">Trace the trail yourself</h3>
    {sources.length > 0 ? (
      <div className="grid md:grid-cols-2 gap-4" data-testid="sources-grid">
        {sources.map((s, i) => (
          <SourceCard
            key={`src-${s.url}`}
            source={s}
            index={i}
            topicSlug={topicSlug}
            topicId={topicId}
          />
        ))}
      </div>
    ) : (
      <p
        className="text-[12px] font-mono text-[#757065] italic"
        data-placeholder="true"
      >
        Editorial pass pending.
      </p>
    )}
    <EvidencePreservation sources={sources} topicSlug={topicSlug} />
  </section>
);

export default SourcesGrid;
